import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
  ReactNode,
  RefObject,
  ForwardRefExoticComponent,
  RefAttributes,
  MutableRefObject,
} from 'react'
import ReactDOM from 'react-dom'
import { Button, Modal } from '@carbon/react'
import styles from './ModalStateManager.module.scss'

/**
 * Context pour partager l'état du modal entre le launcher et le contenu
 */
interface ModalStateContextValue {
  open: boolean
  setOpen: (open: boolean) => void
  launcherRef: RefObject<HTMLElement | null>
}

const ModalStateContext = createContext<ModalStateContextValue | null>(null)

/**
 * Hook pour accéder à l'état du modal
 */
export function useModalState() {
  const context = useContext(ModalStateContext)
  if (!context) {
    throw new Error('useModalState must be used within a ModalStateProvider')
  }
  return context
}

/**
 * Props pour le ModalStateProvider
 */
interface ModalStateProviderProps {
  children: ReactNode
}

/**
 * Provider qui gère l'état du modal.
 * Utilise un pattern qui évite les re-renders inutiles du contenu du modal.
 */
export function ModalStateProvider({ children }: ModalStateProviderProps) {
  const [open, setOpenState] = useState(false)
  const launcherRef = useRef<HTMLElement | null>(null)

  // Utilise useCallback pour éviter les re-renders inutiles
  const setOpen = useCallback((newOpen: boolean) => {
    setOpenState(newOpen)
  }, [])

  const value: ModalStateContextValue = {
    open,
    setOpen,
    launcherRef,
  }

  return (
    <ModalStateContext.Provider value={value}>
      {children}
    </ModalStateContext.Provider>
  )
}

/**
 * Props pour le ModalLauncher
 */
interface ModalLauncherProps {
  children: (props: {
    open: boolean
    setOpen: (open: boolean) => void
    ref: RefObject<HTMLElement | null>
  }) => ReactNode
}

/**
 * Composant pour le bouton/élément qui ouvre le modal
 */
export function ModalLauncher({ children }: ModalLauncherProps) {
  const { open, setOpen, launcherRef } = useModalState()
  return <>{children({ open, setOpen, ref: launcherRef })}</>
}

/**
 * Props pour le ModalContent
 */
interface ModalContentProps {
  children: (props: {
    open: boolean
    setOpen: (open: boolean) => void
    launcherRef: RefObject<HTMLElement | null>
  }) => ReactNode
  /**
   * Si true, utilise un portal pour rendre le contenu dans document.body
   * Cela évite les problèmes de z-index et de positionnement
   */
  usePortal?: boolean
}

/**
 * Composant pour le contenu du modal.
 * Utilise React.memo pour éviter les re-renders inutiles.
 */
export function ModalContent({
  children,
  usePortal = true,
}: ModalContentProps) {
  const { open, setOpen, launcherRef } = useModalState()

  const content = children({ open, setOpen, launcherRef })

  if (usePortal && typeof document !== 'undefined') {
    return ReactDOM.createPortal(content, document.body)
  }

  return <>{content}</>
}

/**
 * Props pour le ModalStateManager (composant wrapper simple)
 */
interface ModalStateManagerProps {
  /**
   * Render prop pour le launcher (bouton qui ouvre le modal)
   */
  renderLauncher: (props: {
    open: boolean
    setOpen: (open: boolean) => void
    ref: RefObject<HTMLElement | null>
  }) => ReactNode
  /**
   * Render prop pour le contenu du modal
   */
  children: (props: {
    open: boolean
    setOpen: (open: boolean) => void
    launcherRef: RefObject<HTMLElement | null>
  }) => ReactNode
  /**
   * Si true, utilise un portal pour le contenu du modal
   */
  usePortal?: boolean
}

/**
 * Composant wrapper qui combine le provider, launcher et content.
 * Version simplifiée pour une migration facile depuis l'ancien pattern.
 *
 * @example
 * ```tsx
 * <ModalStateManager
 *   renderLauncher={({ setOpen, ref }) => (
 *     <Button ref={ref} onClick={() => setOpen(true)}>
 *       Open Modal
 *     </Button>
 *   )}
 * >
 *   {({ open, setOpen, launcherRef }) => (
 *     <Modal
 *       open={open}
 *       onRequestClose={() => setOpen(false)}
 *       launcherButtonRef={launcherRef}
 *     >
 *       Modal content here
 *     </Modal>
 *   )}
 * </ModalStateManager>
 * ```
 */
export function ModalStateManager({
  renderLauncher,
  children,
  usePortal = true,
}: ModalStateManagerProps) {
  return (
    <ModalStateProvider>
      <ModalLauncher>{renderLauncher}</ModalLauncher>
      <ModalContent usePortal={usePortal}>{children}</ModalContent>
    </ModalStateProvider>
  )
}

// ============================================================================
// ModalWithButton - Composant générique prêt à l'emploi
// ============================================================================

/**
 * Interface que le FormContent doit exposer via useImperativeHandle
 */
export interface ModalFormHandle<T = unknown> {
  /** Retourne les données actuelles du formulaire */
  getData: () => T
}

/**
 * Props passées au composant de contenu du formulaire
 */
export interface ModalFormContentProps {
  /** Fermer le modal */
  closeModal: () => void
}

/**
 * Helper pour créer un FormContent avec ref typé
 */
export function createModalFormContent<T>(
  Component: React.ForwardRefRenderFunction<
    ModalFormHandle<T>,
    ModalFormContentProps
  >
): ForwardRefExoticComponent<
  ModalFormContentProps & RefAttributes<ModalFormHandle<T>>
> {
  return forwardRef(Component)
}

/**
 * Props pour le ModalWithButton
 */
export interface ModalWithButtonProps<T = unknown> {
  // Props du bouton
  /** Libellé du bouton */
  buttonLabel: string
  /** Icône du bouton (composant Carbon) */
  buttonIcon?: React.ComponentType
  /** Description de l'icône */
  iconDescription?: string
  /** Taille du bouton */
  buttonSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  /** Style du bouton */
  buttonKind?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'ghost'
    | 'danger'
    | 'danger--tertiary'
    | 'danger--ghost'
  /** Désactiver le bouton */
  disabled?: boolean
  /** ClassName pour le bouton */
  buttonClassName?: string

  // Props du modal
  /** Titre du modal */
  modalHeading: string
  /** Taille du modal */
  modalSize?: 'xs' | 'sm' | 'md' | 'lg'
  /** Texte du bouton principal */
  primaryButtonText?: string
  /** Texte du bouton secondaire */
  secondaryButtonText?: string
  /** Désactiver le bouton principal */
  primaryButtonDisabled?: boolean
  /**
   * Callback quand on clique sur le bouton principal.
   * Reçoit les données du formulaire et une fonction closeModal.
   *
   * @example
   * ```tsx
   * onPrimarySubmit={async (data, closeModal) => {
   *   await saveData(data)
   *   closeModal() // Ferme le modal après la sauvegarde
   * }}
   * ```
   */
  onPrimarySubmit?: (data: T, closeModal: () => void) => void | Promise<void>
  onSecondarySubmit?: (data: T, closeModal: () => void) => void | Promise<void>
  /**
   * Si true, ferme automatiquement le modal après onPrimarySubmit (même si c'est une Promise).
   * Par défaut false - vous devez appeler closeModal() manuellement.
   */
  closeOnSubmit?: boolean
  /** Afficher le bouton d'action */
  passiveModal?: boolean

  /**
   * Composant React pour le contenu du formulaire.
   * Doit utiliser forwardRef et useImperativeHandle pour exposer getData()
   *
   * @example
   * ```tsx
   * const MyFormContent = forwardRef<ModalFormHandle<MyData>, ModalFormContentProps>(
   *   ({ closeModal }, ref) => {
   *     const [data, setData] = useState<MyData>({ name: '' })
   *
   *     useImperativeHandle(ref, () => ({
   *       getData: () => data
   *     }))
   *
   *     return <TextInput value={data.name} onChange={...} />
   *   }
   * )
   * ```
   */
  FormContent: ForwardRefExoticComponent<
    ModalFormContentProps & RefAttributes<ModalFormHandle<T>>
  >
}

/**
 * Composant générique qui combine un bouton déclencheur et un modal.
 * Le contenu du formulaire est passé en paramètre et doit exposer getData() via ref.
 *
 * @example
 * ```tsx
 * // Définir le composant de contenu avec forwardRef
 * const MyFormContent = forwardRef<ModalFormHandle<MyData>, ModalFormContentProps>(
 *   ({ closeModal }, ref) => {
 *     const [data, setData] = useState<MyData>({ name: '' })
 *
 *     useImperativeHandle(ref, () => ({
 *       getData: () => data
 *     }))
 *
 *     return <TextInput value={data.name} onChange={(e) => setData({ name: e.target.value })} />
 *   }
 * )
 *
 * // Utiliser le composant
 * <ModalWithButton
 *   buttonLabel="Ouvrir"
 *   buttonIcon={Settings}
 *   modalHeading="Mon titre"
 *   FormContent={MyFormContent}
 *   onPrimarySubmit={(data) => console.log('Données:', data)}
 * />
 * ```
 */
export function ModalWithButton<T = unknown>({
  // Button props
  buttonLabel,
  buttonIcon,
  iconDescription,
  buttonSize = 'sm',
  buttonKind = 'ghost',
  disabled = false,
  buttonClassName,
  // Modal props
  modalHeading,
  modalSize = 'md',
  primaryButtonText = 'Enregistrer',
  secondaryButtonText = 'Annuler',
  primaryButtonDisabled = false,
  onPrimarySubmit,
  onSecondarySubmit,
  closeOnSubmit = false,
  passiveModal = false,
  // Content
  FormContent,
}: ModalWithButtonProps<T>) {
  const formRef = useRef<ModalFormHandle<T>>(null)

  return (
    <ModalStateManager
      renderLauncher={({ setOpen, ref }) => (
        <Button
          ref={ref as RefObject<HTMLButtonElement>}
          size={buttonSize}
          kind={buttonKind}
          renderIcon={buttonIcon}
          iconDescription={iconDescription}
          onClick={() => setOpen(true)}
          disabled={disabled}
          className={buttonClassName
            ?.split(' ')
            .map(cls => styles[cls] || cls)
            .join(' ')}
        >
          {buttonLabel}
        </Button>
      )}
    >
      {({ open, setOpen, launcherRef }) => {
        const closeModal = () => setOpen(false)

        const handlePrimarySubmit = async () => {
          if (formRef.current && onPrimarySubmit) {
            const data = formRef.current.getData()
            const result = onPrimarySubmit(data, closeModal)

            // Si closeOnSubmit est true, fermer après la Promise
            if (closeOnSubmit) {
              if (result instanceof Promise) {
                await result
              }
              closeModal()
            }
          }
        }

        const handleSecondarySubmit = async () => {
          if (formRef.current && onSecondarySubmit) {
            const data = formRef.current.getData()
            const result = onSecondarySubmit(data, closeModal)
            if (closeOnSubmit) {
              if (result instanceof Promise) {
                await result
              }
              closeModal()
            }
          }
        }
        return (
          <Modal
            launcherButtonRef={launcherRef as RefObject<HTMLButtonElement>}
            open={open}
            onRequestClose={closeModal}
            modalHeading={modalHeading}
            primaryButtonText={primaryButtonText}
            secondaryButtonText={secondaryButtonText}
            onSecondarySubmit={handleSecondarySubmit}
            onRequestSubmit={handlePrimarySubmit}
            primaryButtonDisabled={primaryButtonDisabled}
            size={modalSize}
            passiveModal={passiveModal}
          >
            <FormContent ref={formRef} closeModal={closeModal} />
          </Modal>
        )
      }}
    </ModalStateManager>
  )
}
